<template>
  <InviteContactModal
    :name-disabled="nameDisabled"
    :email-disabled="emailDisabled"
    :show="show"
    :title="title"
    :on-cancel="handleOnCancel"
    :on-confirm="handleOnConfirm"
    :help-text="helpText"
    :error-banner-message="errorBannerMessage"
    :server-validation-errors="serverValidationErrors"
    :save-button-text="saveButtonText"
    :save-button-text-while-working="saveButtonTextWhileWorking"
    data-test="recruiter-candidate-modal"
  >
    <template #commonFields>
      <WdsStack gap-sm direction-column class="tw-mb-6">
        <div class="tw-w-1/2">
          <label>{{ `${$t('First Name')}${!nameDisabled ? '*' : ''}` }}</label>
          <WdsInput
            v-model="name.firstName"
            :placeholder="$t('First name')"
            required
            data-test="recruiter-candidate-first-name-input"
            :disabled="nameDisabled"
            :error="serverValidationErrors['firstName']"
          />
        </div>
        <div class="tw-w-1/2">
          <label>{{ `${$t('Middle Name')}` }}</label>
          <WdsInput
            v-model="name.middleName"
            :placeholder="$t('Middle name')"
            data-test="recruiter-candidate-middle-name-input"
            :disabled="nameDisabled"
            :error="serverValidationErrors['middleName']"
          />
        </div>
        <div class="tw-w-1/2">
          <label>{{ `${$t('Last Name')}${!nameDisabled ? '*' : ''}` }}</label>
          <WdsInput
            v-model="name.lastName"
            :placeholder="$t('Last name')"
            required
            data-test="recruiter-candidate-last-name-input"
            :disabled="nameDisabled"
            :error="serverValidationErrors['lastName']"
          />
        </div>
      </WdsStack>
      <WdsStack direction-column gap-xs class="tw-mb-6">
        <div class="tw-w-1/2">
          <label>{{ `${$t(`Worker's primary email address`)}${!emailDisabled ? '*' : ''}` }}</label>
          <WdsInput
            v-model="email"
            :placeholder="$t('Email address')"
            required
            data-test="recruiter-candidate-email-input"
            :disabled="emailDisabled"
            :error="serverValidationErrors['email']"
          />
        </div>

        <WdsStack v-if="!emailDisabled" direction-column gap-sm>
          <WdsAlert v-if="! managesWorkers" warning bordered>
            <template #default>
              {{ $t(`Please use the worker's primary email address`) }}
            </template>

            <template #description>
              {{
                $t(
                  'This email allows Worksome to generate a compliant contract and ensure you are paid when they are hired',
                )
              }}
            </template>
          </WdsAlert>

          <WdsAlert
            v-if="managesWorkers"
            success
            bordered
            data-test="recruiter-candidate-manages-workers-alert"
          >
            {{ $t('This worker will not be invited to join Worksome, as you manage them') }}
          </WdsAlert>
        </WdsStack>
      </WdsStack>
      <WdsStack gap-lg justify-space-between class="tw-mb-6">
        <div class="tw-w-1/2">
          <label>{{ $t('Job title') }}</label>
          <WdsInput
            v-model="jobTitle"
            data-test="recruiter-candidate-job-title-input"
            :placeholder="$t('Job title')"
          />
        </div>
      </WdsStack>
      <WdsStack direction-column gap-sm class="tw-mb-6">
        <template v-if="!countryExpanded">
          <div>
            <span class="tw-inline-flex tw-items-center tw-gap-1">
              <label>{{ $t('Country') }}</label>
              <WdsTooltip>
                <template #tooltip-content>
                  {{
                    $t(
                      'The country the worker will be based in while working: where they live, or work remotely from. It’s their work location, not their nationality or country of origin.',
                    )
                  }}
                </template>
                <WdsIcon name="help-filled" :size="14" class="tw-text-gray-400 tw-cursor-help" />
              </WdsTooltip>
            </span>
            <WdsStack align-center gap-sm>
              <WdsStack align-center gap-xs>
                <WdsIcon
                  v-if="countryFlagIcon"
                  :name="countryFlagIcon"
                  :size="20"
                  class="tw-flex-shrink-0"
                />
                <span data-test="recruiter-candidate-country-preset">{{ selectedCountryLabel }}</span>
              </WdsStack>
              <WdsButton
                secondary
                sm
                data-test="recruiter-candidate-country-change"
                @click="countryExpanded = true"
              >
                {{ $t('Change') }}
              </WdsButton>
            </WdsStack>
          </div>
        </template>

        <template v-else>
          <div class="tw-w-1/2">
            <span class="tw-inline-flex tw-items-center tw-gap-1">
              <label>{{ $t('Country') }}</label>
              <WdsTooltip>
                <template #tooltip-content>
                  {{
                    $t(
                      'The country the worker will be based in while working: where they live, or work remotely from. It’s their work location, not their nationality or country of origin.',
                    )
                  }}
                </template>
                <WdsIcon name="help-filled" :size="14" class="tw-text-gray-400 tw-cursor-help" />
              </WdsTooltip>
            </span>
            <WdsSelect
              v-model="country"
              :list="getCountries"
              :placeholder="$t('Select country')"
              :error="serverValidationErrors['country']"
              searchable
              can-deselect
              data-test="recruiter-candidate-country-select"
            />
          </div>

          <div v-if="countryHasStates" class="tw-w-1/2">
            <label>{{ $t('State') }}</label>
            <WdsSelect
              v-model="state"
              :list="getStates"
              :placeholder="$t('Select state')"
              :error="serverValidationErrors['state']"
              searchable
              can-deselect
              data-test="recruiter-candidate-state-select"
            />
          </div>

          <WdsAlert icon info>
            {{
              $t(
                'Set the worker’s country so the right compliance applies from the start. Changing it later may require help from support.',
              )
            }}
          </WdsAlert>
        </template>
      </WdsStack>
    </template>
    <template #additionalFields>
      <div>
        <label>{{ `${$t('Rate')}${rateRequired ? '*' : ''}` }}</label>
        <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
        <div v-for="(rateObj, index) in rates" class="tw-mb-2.5">
          <WdsStack :key="index" gap-lg justify-space-between>
            <InputCurrency
              v-model="rateObj.rate"
              :currency="currency"
              :required="rateRequired"
              placeholder="00"
              class="tw-w-1/2"
            />
            <div class="tw-w-1/2 tw-flex tw-items-center tw-gap-1">
              <WdsSelect
                :key="`${rateObj.rateType}-${rates.map((rate) => rate.rateType)}}`"
                :start-selected="rateObj.rateType"
                :placeholder="$t('Select rate type')"
                :list="getSelectOptions(rateObj.rateType)"
                class="tw-flex-grow"
                @select-option="(newRateType) => handleRateTypeSelected(index, newRateType)"
              />
              <WdsButtonGhost v-show="showDeleteRate" @click="deleteRate(index)">
                <WdsIcon name="close" class="tw-text-gray-600" />
              </WdsButtonGhost>
            </div>
          </WdsStack>
          <div
            v-if="serverValidationErrors?.[`${rateMapping[rateObj.rateType]}`]"
            class="tw-text-red-500 p-b-5"
          >
            {{ serverValidationErrors[`${rateMapping[rateObj.rateType]}`] }}
          </div>
        </div>
        <WdsButtonGhost
          v-if="showAddRateTypeButton"
          primary
          class="tw-flex tw-items-center tw-gap-x-2"
          @click="addRateType"
        >
          <WdsIcon name="plus" size="18" />
          {{ $t('Add another rate type') }}
        </WdsButtonGhost>
      </div>
      <div class="tw-mt-6">
        <div>
          <label>{{ `${$t('CV')}${rateRequired ? '*' : ''}` }}</label>
        </div>
        <div
          v-for="file in cvFiles"
          :key="file.id"
          class="tw-flex tw-items-center tw-gap-x-2 tw-mb-2"
        >
          <WdsIcon name="file" :size="16" />
          <a
            :href="laravelNamedRoute(namedRoutes.files.download, { fileId: file.id })"
            class="tw-font-medium tw-grow"
            target="_blank"
          >
            {{ file.name }}
          </a>
          <WdsIcon
            name="close"
            class="tw-cursor-pointer tw-justify-self-end tw-text-gray-600"
            @click="removeCvFile(file.id)"
          />
        </div>
        <PickerFileFilebank
          v-slot="{ openFilePicker }"
          :use-global-id="true"
          :exclude-ids="cvFiles.map(({ id }) => id)"
          @files-selected="uploadCvFiles"
        >
          <WdsButtonGhost
            primary
            class="tw-flex tw-items-center tw-gap-x-2"
            @click="openFilePicker"
          >
            <WdsIcon name="upload" size="18" />
            {{ $t('Upload files') }}
          </WdsButtonGhost>
        </PickerFileFilebank>
      </div>
      <div class="tw-mt-6">
        <div>
          <label>{{ rateRequired ? $t('Additional attachments') : $t('Attachments') }}</label>
        </div>
        <div
          v-for="file in files"
          :key="file.id"
          class="tw-flex tw-items-center tw-gap-x-2 tw-mb-2"
        >
          <WdsIcon name="file" :size="16" />
          <a
            :href="laravelNamedRoute(namedRoutes.files.download, { fileId: file.id })"
            class="tw-font-medium tw-grow"
            target="_blank"
          >
            {{ file.name }}
          </a>
          <WdsIcon
            name="close"
            class="tw-cursor-pointer tw-justify-self-end tw-text-gray-600"
            @click="removeFile(file.id)"
          />
        </div>
        <PickerFileFilebank
          v-slot="{ openFilePicker }"
          :use-global-id="true"
          :exclude-ids="files.map(({ id }) => id)"
          @files-selected="uploadFiles"
        >
          <WdsButtonGhost
            primary
            class="tw-flex tw-items-center tw-gap-x-2"
            @click="openFilePicker"
          >
            <WdsIcon name="upload" size="18" />
            {{ $t('Upload files') }}
          </WdsButtonGhost>
        </PickerFileFilebank>
      </div>
      <div class="tw-mt-6">
        <div>
          <label>{{ $t('Links') }}</label>
        </div>
        <WdsAlert v-if="serverValidationErrors?.['links']" error class="tw-mb-2">
          {{ serverValidationErrors['links'] }}
        </WdsAlert>
        <div v-for="(link, index) in links" :key="link.key" class="tw-mb-2.5">
          <div class="tw-flex tw-items-start tw-gap-1">
            <div class="tw-grow">
              <WdsInput
                v-model="links[index].url"
                prefix-icon="link"
                :data-test="`recruiter-candidate-link-input-${index}`"
                :name="link.key"
                :error="serverValidationErrors[`links.${index}`]"
              />
            </div>
            <div class="tw-mt-px tw-pt-0.5">
              <WdsButtonGhost class="tw-mt-0.5" @click="removeLink(index)">
                <WdsIcon name="close" class="tw-text-gray-600" />
              </WdsButtonGhost>
            </div>
          </div>
        </div>
        <div class="tw-mb-4">
          <WdsButtonGhost
            primary
            data-test="recruiter-candidate-add-link-button"
            class="tw-flex tw-items-center tw-gap-x-2"
            @click="addLink('')"
          >
            <WdsIcon name="plus" size="18" />
            {{ !links.length ? $t('Add link') : $t('Add another link') }}
          </WdsButtonGhost>
        </div>
      </div>
      <div class="tw-mt-6">
        <div>
          <label>{{ $t('Tags') }}</label>
        </div>
        <TypeSelect
          :name="'tags'"
          tag-type="skills"
          :placeholder="`${$t('Eg. Marketing, IT, Expert etc')}...`"
          :selected-in="tags"
          :show-typesuggestions="false"
          :can-add-unsupported-types="true"
        />
      </div>
    </template>
  </InviteContactModal>
</template>

<script>
import {
  WdsAlert,
  WdsStack,
  WdsInput,
  WdsIcon,
  WdsSelect,
  WdsButton,
  WdsButtonGhost,
  WdsTooltip,
} from '@worksome/wds-components'
import gql from 'graphql-tag'

import enums from '@/utils/enum'
import { FormValidation } from '@/utils/formValidation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

import TypeSelect from '@/components/TypeSelect.vue'
import InputCurrency from '@/components/InputCurrency.vue'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import InviteContactModal from '@/components/Contacts/InviteContactModal.vue'
import { useLocation } from '@/composables/useLocation'
import { computeFlagIcon } from '@/utils/ts-tools'
import { getRateAsIntegerAndFraction } from '@/components/utilities/numberFormatting'

export default {
  components: {
    InviteContactModal,
    InputCurrency,
    TypeSelect,
    WdsInput,
    WdsAlert,
    WdsStack,
    WdsSelect,
    WdsButton,
    WdsButtonGhost,
    WdsIcon,
    WdsTooltip,
    PickerFileFilebank,
  },
  props: {
    /**
     * Determines whether the modal is displayed
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * Action to perform on cancel
     */
    onCancel: {
      type: Function,
      default: null,
    },
    /**
     * Action to perform on confirm
     */
    onConfirm: {
      type: Function,
      default: null,
    },
    /**
     * Currency that rate is provided in
     */
    currency: {
      type: String,
      required: true,
    },
    /**
     * Initial rates
     */
    initialRates: {
      type: Object,
      default: () => {},
    },
    initialRateType: {
      type: String,
      default: 'DAILY',
    },
    /**
     * Initial name
     */
    initialName: {
      type: Object,
      default: () => ({
        firstName: '',
        middleName: '',
        lastName: '',
      }),
      validator: (obj) =>
        ['firstName', 'middleName', 'lastName'].every(
          (key) => !!obj[key] && typeof obj[key] === 'string',
        ),
    },
    /**
     * Initial email
     */
    initialEmail: {
      type: String,
      default: null,
    },
    initialJobTitle: {
      type: String,
      default: null,
    },
    initialCountry: {
      type: String,
      default: null,
    },
    initialState: {
      type: String,
      default: null,
    },
    /**
     * Initial tags
     */
    initialTags: {
      type: Array,
      default: () => [],
    },
    /**
     * Initial links
     */
    initialLinks: {
      type: Array,
      default: () => [],
    },
    /**
     * Initial files
     */
    initialFiles: {
      type: Array,
      default: () => [],
    },
    /**
     * Whether the name field is disabled
     */
    nameDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the email field is disabled
     */
    emailDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Modal Title
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Modal help text
     */
    helpText: {
      type: String,
      default: null,
    },
    /**
     * Error text to display if a generic error occurs
     */
    errorText: {
      type: String,
      default: 'An error occurred',
    },
    /**
     * Text to display on save button
     */
    saveButtonText: {
      type: String,
      default: undefined,
    },
    /**
     * Text to display on save button while working
     */
    saveButtonTextWhileWorking: {
      type: String,
      default: undefined,
    },
    /**
     * If the recruiter/SA manages their workers
     */
    managesWorkers: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether at least one rate is required (e.g. for NL staffing agencies)
     */
    rateRequired: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { getCountries, getStates, setSelectedCountry, setSelectedState, countryHasStates } =
      useLocation()

    return { getCountries, getStates, setSelectedCountry, setSelectedState, countryHasStates }
  },
  data() {
    const rateConstants = {
      HOURLY: 'HOURLY',
      DAILY: 'DAILY',
      MONTHLY: 'MONTHLY',
    }

    return {
      namedRoutes,
      rates: this.initializeRateFields(),
      serverValidationErrors: {},
      errorBannerMessage: '',
      rateConstants,
      selectOptions: [
        { value: rateConstants.HOURLY, label: $t('Per hour') },
        { value: rateConstants.DAILY, label: $t('Per day') },
        { value: rateConstants.MONTHLY, label: $t('Per month') },
      ],
      email: this.initialEmail || '',
      country: null,
      state: null,
      countryExpanded: false,
      name: { ...this.initialName },
      jobTitle: this.initialJobTitle || '',
      tags: [...this.initialTags],
      links: [...this.initialLinks],
      files: this.initialFiles.filter((file) => !file.tags?.some((tag) => tag.name === 'CV')),
      cvFiles: this.initialFiles.filter((file) => file.tags?.some((tag) => tag.name === 'CV')),
      rateMapping: {
        [rateConstants.HOURLY]: 'hourlyRate',
        [rateConstants.DAILY]: 'dailyRate',
        [rateConstants.MONTHLY]: 'monthlyRate',
      },
    }
  },
  computed: {
    showAddRateTypeButton() {
      return this.rates.length < 3
    },
    showDeleteRate() {
      return this.rates.length > 1
    },
    selectedCountryLabel() {
      const code = this.country?.value
      if (!code) {
        return ''
      }

      return this.getCountries.find((option) => option.value === code)?.label ?? code
    },
    countryFlagIcon() {
      return this.country?.value ? computeFlagIcon(this.country.value) : null
    },
  },
  watch: {
    name() {
      this.serverValidationErrors = {}
    },
    email() {
      this.serverValidationErrors = {}
    },
    country(selected) {
      this.setSelectedCountry(selected?.value?.toUpperCase() ?? '')
      // State belongs to the chosen country, so clear it whenever country changes.
      this.state = null
    },
    state(selected) {
      this.setSelectedState(selected?.value?.toUpperCase() ?? '')
    },
  },
  created() {
    this.initCountrySelection()
  },
  methods: {
    laravelNamedRoute,
    initCountrySelection() {
      // When editing, prefill the worker's saved country; otherwise default to
      // the supplier's own country (the common case is a worker based where the
      // supplier is). Stay collapsed to a summary unless there is no preset, or
      // the country also needs a state (US/CA/AU) where picking the right one matters.
      const presetCountry = this.initialCountry ?? currentAccount?.country
      this.country = presetCountry ? { value: presetCountry.toUpperCase() } : null
      this.setSelectedCountry(this.country?.value ?? '')
      this.countryExpanded = !this.country || this.countryHasStates

      // Setting the country triggers the watcher that clears the state, so apply
      // the saved state afterwards.
      this.$nextTick(() => {
        this.state = this.initialState ? { value: this.initialState.toUpperCase() } : null
      })
    },
    resetForm() {
      this.serverValidationErrors = {}
      this.errorBannerMessage = ''

      this.name = { ...this.initialName }
      this.email = this.initialEmail || ''
      this.initCountrySelection()
      this.jobTitle = this.initialJobTitle || ''
      this.tags = this.initialTags || []
      this.links = this.initialLinks || []
      this.files = (this.initialFiles || []).filter((file) => !file.tags?.some((tag) => tag.name === 'CV'))
      this.cvFiles = (this.initialFiles || []).filter((file) => file.tags?.some((tag) => tag.name === 'CV'))
      this.rates = this.initializeRateFields()
    },
    handleOnCancel() {
      this.resetForm()
      this.onCancel()
    },
    getRateAsDecimal(rateObj) {
      return rateObj && (rateObj.integer || rateObj.fraction)
        ? parseFloat(`${rateObj.integer || 0}.${rateObj.fraction || 0}`)
        : null
    },
    async handleOnConfirm() {
      this.errorBannerMessage = ''

      // Find each rate type (if it exists) and format it
      const getRateForSubmit = (rateType) =>
        this.getRateAsDecimal(this.rates.find((rate) => rate.rateType === rateType)?.rate)

      const hourlyRate = getRateForSubmit(this.rateConstants.HOURLY)
      const dailyRate = getRateForSubmit(this.rateConstants.DAILY)
      const monthlyRate = getRateForSubmit(this.rateConstants.MONTHLY)

      try {
        await this.syncCvFileTags()
      } catch {
        this.errorBannerMessage = this.errorText
        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.errorText,
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
        return
      }

      try {
        await this.onConfirm({
          ...this.name,
          email: this.email,
          country: this.country?.value ?? null,
          state: this.state?.value ?? null,
          hourlyRate,
          monthlyRate,
          dailyRate,
          jobTitle: this.jobTitle,
          tags: this.tags,
          links: this.links.filter((link) => link.url).map((link) => link.url),
          files: [...this.files, ...this.cvFiles].map((file) => file.id),
        })
        this.handleOnCancel()
      } catch (error) {
        const { graphQLErrors } = error
        if (FormValidation.containsFormValidations(graphQLErrors)) {
          this.serverValidationErrors = FormValidation.getSingularGraphqlErrors(
            graphQLErrors,
            'input.',
          )
        } else if (FormValidation.containsErrorMessage(graphQLErrors)) {
          this.errorBannerMessage = graphQLErrors[0].message
        } else {
          this.errorBannerMessage = this.errorText
          this.$store.commit('sendNotification', {
            type: enums.notification.types.ERROR,
            msg: this.errorText,
            sender: this,
            severity: enums.notification.severity.CRITICAL,
          })
        }
      }
    },
    handleRateTypeSelected(index, { value: newRateType }) {
      this.rates[index].rateType = newRateType
    },
    addLink(url) {
      const hasEmptyRows = this.links.some((link) => link.url === '')

      if (!hasEmptyRows) {
        this.links = [...this.links, { key: this.links.length, url }]
      }
    },
    removeLink(linkIndex) {
      this.links.splice(linkIndex, 1)
    },
    uploadFiles(files) {
      this.files = [
        ...this.files,
        ...files.map((file) => ({
          name: file.name,
          id: file.id,
          storageName: file.storageName,
        })),
      ]
    },
    removeFile(fileGlobalId) {
      this.files = this.files.filter((file) => file.id !== fileGlobalId)
    },
    uploadCvFiles(files) {
      this.cvFiles = [
        ...this.cvFiles,
        ...files.map((file) => ({
          name: file.name,
          id: file.id,
          storageName: file.storageName,
        })),
      ]
    },
    removeCvFile(fileGlobalId) {
      this.cvFiles = this.cvFiles.filter((file) => file.id !== fileGlobalId)
    },
    async syncCvFileTags() {
      const attachMutation = gql`
        mutation attachFileTags($file: FileIntOrIDInput!, $tags: [String]) {
          attachFileTags(file: $file, tags: $tags)
        }
      `
      const detachMutation = gql`
        mutation detachFileTags($file: FileIntOrIDInput!, $tags: [String]) {
          detachFileTags(file: $file, tags: $tags)
        }
      `

      const tagPromises = [
        ...this.cvFiles.map((file) =>
          this.$apollo.mutate({
            mutation: attachMutation,
            variables: { file: { globalId: file.id }, tags: ['CV'] },
          }),
        ),
        ...this.files
          .filter((file) => file.tags?.some((tag) => tag.name === 'CV'))
          .map((file) =>
            this.$apollo.mutate({
              mutation: detachMutation,
              variables: { file: { globalId: file.id }, tags: ['CV'] },
            }),
          ),
      ]

      await Promise.all(tagPromises)
    },
    addRateType() {
      const existingRateTypes = this.rates
        .filter((rate) => rate.rateType)
        .map((rate) => rate.rateType)
      const rateType =
        existingRateTypes.length > 1
          ? // eslint-disable-next-line @typescript-eslint/no-shadow -- Baseline eslint ignore please fix if working on this
            Object.keys(this.rateMapping).find((rateType) => !existingRateTypes.includes(rateType))
          : undefined
      this.rates.push({
        rateType,
        rate: {},
      })
    },
    getSelectOptions(rateType) {
      return this.selectOptions.filter(
        (option) =>
          option.value === rateType ||
          !this.rates.find((rateObj) => rateObj.rateType === option.value),
      )
    },
    deleteRate(rateTypeIndex) {
      this.rates.splice(rateTypeIndex, 1)
    },
    initializeRateFields() {
      return this.initialRates &&
        Object.keys(this.initialRates).filter((rate) => this.initialRates[rate]).length
        ? Object.keys(this.initialRates)
            .filter((rate) => this.initialRates[rate])
            .map((rateType) => ({
              rateType: rateType.toUpperCase(),
              rate: getRateAsIntegerAndFraction(this.initialRates[rateType]),
            }))
        : [
            {
              rateType: this.initialRateType,
              rate: {},
            },
          ]
    },
  },
}
</script>

<style lang="scss">
.create-candidate-rate {
  width: 200px;
  & label {
    text-transform: capitalize;
  }
}
</style>
